import React, { useEffect, useState } from "react";
import MaterialTable from "material-table";
import DeleteIcon from "@material-ui/icons/Delete";
import SearchIcon from "@material-ui/icons/Search";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import CloseIcon from "@mui/icons-material/Close";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import SkipNextIcon from "@mui/icons-material/SkipNext";
import SkipPreviousIcon from "@mui/icons-material/SkipPrevious";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import {
  ThemeProvider,
  Alert,
  AlertTitle,
  Divider,
  Button,
} from "@mui/material";
import {
  countInvalidAll,
  countValidAll,
  getCheckedAddress,
} from "./Utilities/api";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import { Pie } from "react-chartjs-2";
import { useIsDarkMode } from "../../theme/ThemeContext";
import { useAppMuiTheme } from "../../theme/MuiThemeBridge";
import WhereToVoteIcon from "@mui/icons-material/WhereToVote";
import { PageHeader, PageShell } from "../../components/UI/Page";

ChartJS.register(ArcElement, Tooltip, Legend);

const AddressValid = () => {
  const isDark = useIsDarkMode();
  // A nested MUI ThemeProvider replaces the app-wide one, so this local theme
  // has to carry palette.mode itself or MUI internals (pagination, search
  // field, menus) revert to light on a dark page.
  const defaultMaterialTheme = useAppMuiTheme();
  const [dataStore, setDataStore] = useState([]);
  const [userSelected, setUser] = useState("ALL");

  const [countInvalid, setInvalid] = useState(0);
  const [countValid, setValid] = useState(0);

  useEffect(() => {
    getCheckedAddress(setDataStore, userSelected);
    countInvalidAll(setInvalid, userSelected);
    countValidAll(setValid, userSelected);
  }, [userSelected]);

  const data = {
    labels: [`Invalid (${countInvalid})`, `Valid (${countValid})`],
    datasets: [
      {
        label: "# of Votes",
        data: [countInvalid, countValid],
        backgroundColor: ["rgba(255, 99, 132, 0.2)", "rgba(54, 162, 235, 0.2)"],
        borderColor: ["rgba(255, 99, 132, 1)", "rgba(54, 162, 235, 1)"],
        borderWidth: 1,
      },
    ],
  };

  return (
    <PageShell size="2xl">
      <PageHeader
        icon={WhereToVoteIcon}
        tone="slate"
        eyebrow="Admin"
        title="Address Validation"
        description="Order addresses that have been checked. Click a salesperson's name to filter the table and chart to their orders."
      />
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="app-card min-w-0 overflow-hidden rounded-2xl border lg:col-span-2">
          <div className="overflow-x-auto">
            <ThemeProvider theme={defaultMaterialTheme}>
              <MaterialTable
                style={{
                  backgroundColor: "transparent",
                  color: "var(--app-text)",
                  boxShadow: "none",
                }}
                options={{
                  pageSize: 10, // make initial page size
                  emptyRowsWhenPaging: false, // To avoid of having empty rows
                  pageSizeOptions: [5, 10, 15, 25],
                  headerStyle: {
                    backgroundColor: "var(--app-surface-bg)",
                    color: "var(--app-text-muted)",
                    fontWeight: 600,
                    borderBottom: "1px solid var(--app-divider)",
                  },
                  searchFieldStyle: {
                    color: "var(--app-text)",
                  },
                  rowStyle: (rowData) => {
                    return {
                      fontSize: 14,
                    };
                  },
                }}
                columns={[
                  {
                    title: "Order#",
                    field: "orderNumber",
                  },
                  {
                    title: "Status",
                    field: "status",
                  },
                  {
                    title: "Match",
                    field: "match_type",
                  },
                  {
                    title: "Address",
                    field: "address",
                  },
                  {
                    title: "Salesperson",
                    field: "user",
                    render: (rowData) => {
                      return (
                        <Button
                          onClick={() => {
                            setUser(rowData.user);
                          }}
                        >
                          {rowData.user}
                        </Button>
                      );
                    },
                  },
                  {
                    title: "Branch",
                    field: "branch",
                  },
                ]}
                data={dataStore}
                title={
                  userSelected === "ALL" ? (
                    `Address Validation - ${userSelected}`
                  ) : (
                    <div className="flex items-center">
                      <p>Address Validation - {userSelected} </p>
                      <Button
                        onClick={() => {
                          setUser("ALL");
                        }}
                      >
                        <CloseIcon
                          className="text-red-600 dark:text-red-400"
                        />
                      </Button>
                    </div>
                  )
                }
                icons={{
                  Clear: (props) => (
                    <DeleteIcon className="text-slate-700 dark:text-slate-200" />
                  ),
                  Search: (props) => (
                    <SearchIcon className="text-slate-700 dark:text-slate-200" />
                  ),
                  ResetSearch: (props) => (
                    <DeleteIcon className="text-slate-700 dark:text-slate-200" />
                  ),
                  NextPage: (props) => (
                    <NavigateNextIcon className="text-slate-700 dark:text-slate-200" />
                  ),
                  PreviousPage: (props) => (
                    <NavigateBeforeIcon className="text-slate-700 dark:text-slate-200" />
                  ),
                  FirstPage: (props) => (
                    <SkipPreviousIcon className="text-slate-700 dark:text-slate-200" />
                  ),
                  LastPage: (props) => (
                    <SkipNextIcon className="text-slate-700 dark:text-slate-200" />
                  ),
                  SortArrow: (props) => (
                    <ArrowDropDownIcon className="text-slate-700 dark:text-slate-200" />
                  ),
                }}
                localization={{
                  body: {
                    emptyDataSourceMessage: (
                      <div className="flex justify-center">
                        <Alert severity="success">
                          <div>
                            <AlertTitle>No Data</AlertTitle>
                            If you think this is a mistake please
                            contact <strong>Help Desk</strong>.
                          </div>
                          <Divider />
                        </Alert>
                      </div>
                    ),
                  },
                }}
              />
            </ThemeProvider>
          </div>
        </div>
        <div className="min-w-0">
          <div className="app-card rounded-2xl border p-5 lg:sticky lg:top-24">
            <h2 className="font-semibold">
              {userSelected === "ALL" ? "All salespeople" : userSelected}
            </h2>
            <p className="app-muted mb-4 text-sm tabular-nums">
              {countValid} valid · {countInvalid} invalid
            </p>
            <Pie
              style={{ height: 100 }}
              data={data}
              options={{
                plugins: {
                  legend: {
                    labels: {
                      // Chart.js draws to canvas, so `dark:` utilities
                      // can't reach the legend text.
                      color: isDark ? "#e2e8f0" : "#334155",
                    },
                  },
                },
              }}
            />
          </div>
        </div>
      </div>
    </PageShell>
  );
};

export default AddressValid;
